import React, { useMemo } from "react";
import { CalendarEvent } from "./calendar-types";
import {
    EVENT_ROW_SPAN,
    HOURS,
    SLOTS_PER_HOUR,
    SUBROWS_PER_SLOT,
    computeOverlapLayout,
    formatDateKey,
    formatTime,
    isSameDay,
    parseDateTimeToTimeStr,
    parseTimeToRow,
} from "./calendar-utils";

interface DayGridProps {
    day: Date;
    today: Date;
    eventsByDate: Map<string, CalendarEvent[]>;
    onEventClick: (event: CalendarEvent) => void;
}

const DAY_ABBREVS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export const DayGrid: React.FC<DayGridProps> = ({
    day,
    today,
    eventsByDate,
    onEventClick,
}) => {
    const dateKey = formatDateKey(day);
    const isToday = isSameDay(day, today);

    const { allDayEvents, timedEvents } = useMemo(() => {
        const dayEvents = eventsByDate.get(dateKey) || [];
        const allDay: CalendarEvent[] = [];
        const timed: CalendarEvent[] = [];
        for (const evt of dayEvents) {
            const hasTiming =
                evt.kind === "tour" ? !!evt.data.tourTime : !!evt.data.dueDateTime;
            if (hasTiming) {
                timed.push(evt);
            } else {
                allDay.push(evt);
            }
        }
        return { allDayEvents: allDay, timedEvents: timed };
    }, [eventsByDate, dateKey]);

    const laidOutEvents = useMemo(() => {
        const withRows = timedEvents
            .map(evt => {
                const timeStr =
                    evt.kind === "tour"
                        ? evt.data.tourTime
                        : parseDateTimeToTimeStr(evt.data.dueDateTime);
                return {
                    event: evt,
                    row: parseTimeToRow(timeStr),
                };
            })
            .filter(
                (
                    item,
                ): item is {
                    event: CalendarEvent;
                    row: number;
                } => item.row !== null,
            );
        return computeOverlapLayout(withRows);
    }, [timedEvents]);

    return (
        <div className="isolate flex flex-auto flex-col overflow-auto bg-white border border-gray-200 rounded-lg">
            <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
                <div className="flex items-center">
                    <div className="w-14 flex-none flex flex-col items-center py-2">
                        <span className="text-xs font-medium text-gray-500">
                            {DAY_ABBREVS[day.getDay()]}
                        </span>
                        <span
                            className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full text-lg font-semibold ${
                                isToday ? "bg-brand-600 text-white" : "text-gray-900"
                            }`}>
                            {day.getDate()}
                        </span>
                    </div>
                    {allDayEvents.length > 0 && (
                        <div className="flex-1 min-w-0 py-1 pr-2 space-y-1">
                            {allDayEvents.map(evt => {
                                const id =
                                    evt.kind === "tour" ? evt.data.id : evt.data.id;
                                const label =
                                    evt.kind === "tour"
                                        ? evt.data.communityName
                                        : evt.data.text;
                                const isTour = evt.kind === "tour";
                                return (
                                    <button
                                        key={id}
                                        onClick={() => onEventClick(evt)}
                                        data-testid={
                                            isTour
                                                ? "calendar-tour-event"
                                                : "calendar-task-event"
                                        }
                                        className={`block w-full text-left px-2 py-1.5 text-sm rounded truncate ${
                                            isTour
                                                ? "bg-brand-600 text-white hover:bg-brand-700"
                                                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                        }`}>
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex max-w-full flex-none flex-col">
                <div className="flex flex-auto">
                    <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
                    <div className="grid flex-auto grid-cols-1 grid-rows-1 min-w-0">
                        <div
                            style={{
                                gridTemplateRows: `repeat(${HOURS.length * SLOTS_PER_HOUR}, minmax(1.5rem, 1fr))`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100">
                            <div className="row-end-1 h-7" />
                            {HOURS.map((hour, i) => (
                                <React.Fragment key={i}>
                                    <div>
                                        <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
                                            {hour}
                                        </div>
                                    </div>
                                    <div />
                                </React.Fragment>
                            ))}
                        </div>

                        <ol
                            style={{
                                gridTemplateRows: `1.75rem repeat(${HOURS.length * SLOTS_PER_HOUR * SUBROWS_PER_SLOT}, minmax(0, 1fr)) auto`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 z-10 grid grid-cols-1 pr-2">
                            {laidOutEvents.map(({ event: evt, layout }) => {
                                const widthPct = 100 / layout.totalColumns;
                                const leftPct = layout.columnIndex * widthPct;
                                const id =
                                    evt.kind === "tour" ? evt.data.id : evt.data.id;
                                const isTour = evt.kind === "tour";
                                const label =
                                    evt.kind === "tour"
                                        ? evt.data.communityName
                                        : evt.data.text;
                                const timeDisplay =
                                    evt.kind === "tour"
                                        ? formatTime(evt.data.tourTime)
                                        : formatTime(
                                              parseDateTimeToTimeStr(
                                                  evt.data.dueDateTime,
                                              ),
                                          );
                                return (
                                    <li
                                        key={id}
                                        style={{
                                            gridRow: `${layout.row} / span ${EVENT_ROW_SPAN}`,
                                            gridColumnStart: 1,
                                        }}
                                        className="relative mt-px pointer-events-none">
                                        <button
                                            onClick={() => onEventClick(evt)}
                                            data-testid={
                                                isTour
                                                    ? "calendar-tour-event"
                                                    : "calendar-task-event"
                                            }
                                            style={{
                                                left: `${leftPct}%`,
                                                width: `${widthPct}%`,
                                            }}
                                            className={`pointer-events-auto group absolute inset-y-px flex flex-col overflow-hidden rounded-md p-2 text-sm text-left ${
                                                isTour
                                                    ? "bg-brand-600 hover:bg-brand-700"
                                                    : "bg-emerald-600 hover:bg-emerald-700"
                                            }`}>
                                            <p className="font-semibold text-white">
                                                {label}
                                            </p>
                                            <p
                                                className={
                                                    isTour
                                                        ? "text-brand-100"
                                                        : "text-emerald-100"
                                                }>
                                                {timeDisplay}
                                            </p>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};
